import type { z } from "zod";
import type { Channel } from "./envelope.ts";

/**
 *   request       answered by "<type>.result" or "error" with the same id
 *   notification  fire-and-forget, browser → agent
 *   event         streaming, agent → browser or server → browser
 */
export interface RequestDef<
  C extends Channel = Channel,
  T extends string = string,
  Req extends z.ZodType = z.ZodType,
  Res extends z.ZodType = z.ZodType,
> {
  kind: "request";
  channel: C;
  type: T;
  request: Req;
  response: Res;
}

export interface PayloadDef<
  K extends "notification" | "event" = "notification" | "event",
  C extends Channel = Channel,
  T extends string = string,
  P extends z.ZodType = z.ZodType,
> {
  kind: K;
  channel: C;
  type: T;
  payload: P;
}

export function request<C extends Channel, T extends string, Req extends z.ZodType, Res extends z.ZodType>(
  channel: C,
  type: T,
  req: Req,
  res: Res,
): RequestDef<C, T, Req, Res> {
  return { kind: "request", channel, type, request: req, response: res };
}

export function notification<C extends Channel, T extends string, P extends z.ZodType>(
  channel: C,
  type: T,
  payload: P,
): PayloadDef<"notification", C, T, P> {
  return { kind: "notification", channel, type, payload };
}

export function event<C extends Channel, T extends string, P extends z.ZodType>(
  channel: C,
  type: T,
  payload: P,
): PayloadDef<"event", C, T, P> {
  return { kind: "event", channel, type, payload };
}
